import React, { useCallback, useState, useEffect, useRef } from 'react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { InputText } from 'primereact/inputtext';
import { MarketLandscapeItem } from 'services/bidAnalysis/bidAnalysisServices';
import classes from '../../bidAnalysisTemplate.module.scss';
import tableClasses from './MarketLandscapeTable.module.scss';

// Ultra-fast input - no memoization, no comparisons, just direct updates
const FastInput: React.FC<{
  value: string;
  onChange: (value: string) => void;
  field: string;
  rowIndex: number;
}> = ({ value, onChange }) => {
  return (
    <InputText
      value={value || ''}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
      style={{ width: '100%' }}
    />
  );
};

interface MarketLandscapeTableProps {
  data: MarketLandscapeItem[];
  isEditMode: boolean;
  onDataChange: (data: MarketLandscapeItem[]) => void;
  onDeleteRow: (index: number) => void;
  onAddRow: () => void;
  onSave?: (data: MarketLandscapeItem[]) => void;
  onCancel?: () => void;
  isSaving?: boolean;
}

export const MarketLandscapeTable: React.FC<MarketLandscapeTableProps> = ({
  data,
  isEditMode,
  onDataChange: _onDataChange,
  onDeleteRow,
  onAddRow,
  onSave,
  onCancel,
  isSaving = false,
}) => {
  // Local state for editing - syncs with props but allows fast local updates
  const [localData, setLocalData] = useState<MarketLandscapeItem[]>(data);
  const prevDataLengthRef = useRef(data.length);
  const prevIsEditModeRef = useRef(isEditMode);

  // Sync local data when:
  // 1. Data length changes (row added/deleted externally)
  // 2. Exiting edit mode (to get latest data, including sorted data)
  // 3. Initial mount
  useEffect(() => {
    const dataLengthChanged = data.length !== prevDataLengthRef.current;
    const exitedEditMode = prevIsEditModeRef.current && !isEditMode;
    
    if (dataLengthChanged || exitedEditMode) {
      setLocalData([...data]); // Create new array reference to ensure update
    }
    
    prevDataLengthRef.current = data.length;
    prevIsEditModeRef.current = isEditMode;
  }, [data, isEditMode]);


  // Optimized field change handler - updates ONLY local state immediately for zero latency
  // Uses functional update to avoid dependency on localData
  const handleFieldChange = useCallback(
    (rowIndex: number, field: keyof MarketLandscapeItem, value: string | boolean | null) => {
      setLocalData((prevData) => {
        return prevData.map((row, index) => {
          if (index === rowIndex) {
            return {
              ...row,
              [field]: value,
            };
          }
          return row;
        });
      });
      // NO parent update during typing - this eliminates all latency
    },
    []
  );

  // Use localData for rendering to avoid lag
  // Don't memoize - let React handle it naturally for better performance
  const displayData = isEditMode ? localData : data;

  // Body templates - direct access to localData, no memoization overhead
  const supplierBodyTemplate = (rowData: MarketLandscapeItem, { rowIndex }: { rowIndex: number }) => {
    if (isEditMode) {
      const currentValue = localData[rowIndex]?.supplier || '';
      return (
        <FastInput
          value={currentValue}
          onChange={(value) => handleFieldChange(rowIndex, 'supplier', value)}
          field="supplier"
          rowIndex={rowIndex}
        />
      );
    }
    return <span>{rowData.supplier || '-'}</span>;
  };

  const apiBodyTemplate = (rowData: MarketLandscapeItem, { rowIndex }: { rowIndex: number }) => {
    if (isEditMode) {
      const currentValue = localData[rowIndex]?.api || '';
      return (
        <FastInput
          value={currentValue}
          onChange={(value) => handleFieldChange(rowIndex, 'api', value)}
          field="api"
          rowIndex={rowIndex}
        />
      );
    }
    return <span>{rowData.api || '-'}</span>;
  };

  const mfgCoBodyTemplate = (rowData: MarketLandscapeItem, { rowIndex }: { rowIndex: number }) => {
    if (isEditMode) {
      const currentValue = localData[rowIndex]?.mfg_co || '';
      return (
        <FastInput
          value={currentValue}
          onChange={(value) => handleFieldChange(rowIndex, 'mfg_co', value)}
          field="mfg_co"
          rowIndex={rowIndex}
        />
      );
    }
    return <span>{rowData.mfg_co || '-'}</span>;
  };

  const notesBodyTemplate = (rowData: MarketLandscapeItem, { rowIndex }: { rowIndex: number }) => {
    if (isEditMode) {
      const currentValue = localData[rowIndex]?.note || '';
      return (
        <FastInput
          value={currentValue}
          onChange={(value) => handleFieldChange(rowIndex, 'note', value)}
          field="note"
          rowIndex={rowIndex}
        />
      );
    }
    return <span>{rowData.note || '-'}</span>;
  };

  const compliantBodyTemplate = (rowData: MarketLandscapeItem, { rowIndex }: { rowIndex: number }) => {
    const currentRow = isEditMode ? localData[rowIndex] : rowData;
    const isCompliant = currentRow?.is_compliant;
    
    // Determine cell background color
    const cellStyle: React.CSSProperties = { 
      padding: '0.5rem', 
      textAlign: 'center',
      minHeight: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    };
    if (isCompliant === true) {
      cellStyle.backgroundColor = '#d1fae5'; // Light green
    } else if (isCompliant === false) {
      cellStyle.backgroundColor = '#fee2e2'; // Light red
    }

    if (isEditMode) {
      return (
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', alignItems: 'center', padding: '0.25rem' }}>
          <button
            type="button"
            onClick={() => handleFieldChange(rowIndex, 'is_compliant', null)}
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '4px',
              border: isCompliant === null ? '2px solid #4d2e6d' : '2px solid #d1d5db',
              backgroundColor: '#ffffff',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              if (isCompliant !== null) {
                e.currentTarget.style.borderColor = '#4d2e6d';
              }
            }}
            onMouseLeave={(e) => {
              if (isCompliant !== null) {
                e.currentTarget.style.borderColor = '#d1d5db';
              }
            }}
            title="Not Set"
          />
          <button
            type="button"
            onClick={() => handleFieldChange(rowIndex, 'is_compliant', true)}
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '4px',
              border: isCompliant === true ? '2px solid #4d2e6d' : '2px solid #d1d5db',
              backgroundColor: '#d1fae5',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              if (isCompliant !== true) {
                e.currentTarget.style.borderColor = '#4d2e6d';
                e.currentTarget.style.backgroundColor = '#a7f3d0';
              }
            }}
            onMouseLeave={(e) => {
              if (isCompliant !== true) {
                e.currentTarget.style.borderColor = '#d1d5db';
                e.currentTarget.style.backgroundColor = '#d1fae5';
              }
            }}
            title="Compliant"
          />
          <button
            type="button"
            onClick={() => handleFieldChange(rowIndex, 'is_compliant', false)}
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '4px',
              border: isCompliant === false ? '2px solid #4d2e6d' : '2px solid #d1d5db',
              backgroundColor: '#fee2e2',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              if (isCompliant !== false) {
                e.currentTarget.style.borderColor = '#4d2e6d';
                e.currentTarget.style.backgroundColor = '#fecaca';
              }
            }}
            onMouseLeave={(e) => {
              if (isCompliant !== false) {
                e.currentTarget.style.borderColor = '#d1d5db';
                e.currentTarget.style.backgroundColor = '#fee2e2';
              }
            }}
            title="Not Compliant"
          />
        </div>
      );
    }

    // View mode - show colored cell only (no icons)
    return <div style={cellStyle}></div>;
  };

  const actionsBodyTemplate = (rowData: MarketLandscapeItem, { rowIndex }: { rowIndex: number }) => {
    if (!isEditMode) {
      return null;
    }
    return (
      <Button
        icon="pi pi-trash"
        className="p-button-danger p-button-text"
        onClick={() => onDeleteRow(rowIndex)}
        tooltip="Delete row"
        tooltipOptions={{ position: 'top' }}
      />
    );
  };

  return (
    <div>
      {/* Always show toolbar with Add Row button, and Save/Cancel when in edit mode */}
      <div className={tableClasses.stickyToolbar}>
        <Button
          label="Add Row"
          icon="pi pi-plus"
          onClick={onAddRow}
          className="p-button-outlined"
          size="small"
          disabled={!isEditMode}
        />
        {isEditMode && (
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {onCancel && (
              <Button
                label="Cancel"
                icon="pi pi-times"
                onClick={onCancel}
                className="p-button-outlined"
                disabled={isSaving}
                size="small"
              />
            )}
            {onSave && (
              <Button
                label="Save Changes"
                icon="pi pi-check"
                onClick={() => {
                  // Pass current localData directly to save handler
                  // This ensures all user edits are included
                  onSave(localData);
                }}
                loading={isSaving}
                disabled={isSaving}
                size="small"
                style={{ backgroundColor: '#4d2e6d', borderColor: '#4d2e6d', color: 'white' }}
              />
            )}
          </div>
        )}
      </div>
      {data.length === 0 && !isEditMode ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
          <i className="pi pi-table" style={{ fontSize: '2rem', marginBottom: '0.5rem' }}></i>
          <p>No market landscape data available.</p>
        </div>
      ) : (
        <DataTable
          key={`market-landscape-${isEditMode ? 'edit' : 'view'}-${data.length}`}
          value={displayData}
          className={classes.purpleTable}
          scrollable
          scrollHeight="500px"
          style={{ width: '100%' }}
          rowHover={false}
          reorderableColumns={false}
          resizableColumns={false}
          lazy={false}
        >
          <Column
            field="supplier"
            header="Supplier"
            body={supplierBodyTemplate}
            style={{ minWidth: '200px' }}
            key="supplier-col"
          />
          <Column 
            field="api" 
            header="API" 
            body={apiBodyTemplate} 
            style={{ minWidth: '150px' }}
            key="api-col"
          />
          <Column
            field="mfg_co"
            header="MFG Co"
            body={mfgCoBodyTemplate}
            style={{ minWidth: '150px' }}
            key="mfg_co-col"
          />
          <Column 
            field="note" 
            header="Notes" 
            body={notesBodyTemplate} 
            style={{ minWidth: '200px' }}
            key="note-col"
          />
          <Column
            field="is_compliant"
            header=""
            body={compliantBodyTemplate}
            style={{ minWidth: '150px', textAlign: 'center' }}
            key="compliant-col"
          />
          {isEditMode && (
            <Column
              header="Actions"
              body={actionsBodyTemplate}
              style={{ width: '100px', textAlign: 'center' }}
            />
          )}
        </DataTable>
      )}
      {data.length === 0 && isEditMode && (
        <div style={{ padding: '1rem', textAlign: 'center', color: '#6b7280' }}>
          <p>No rows yet. Click "Add Row" to add market landscape data.</p>
        </div>
      )}
    </div>
  );
};
